<template>
    <div class='customer-page'>
        <section v-if='!selectedProjectName' class='customer-frame project-entry'>
            <header class='customer-topbar'>
                <h1>{{ $t('customer.title') }}</h1>
            </header>
            
            <div class='project-entry-body'>
                <h2>{{ $t('customer.chooseGameTitle') }}</h2>
                <p>{{ $t('customer.chooseGameTip') }}</p>
                <el-input
                    v-model='projectSearch'
                    class='project-search'
                    size='small'
                    clearable
                    prefix-icon='el-icon-search'
                    :placeholder="$t('customer.searchGamePlaceholder')" />
                
                <div v-if='projectLoading' class='empty-state'>{{ $t('common.loading') }}</div>
                <div v-else-if='projectList.length === 0' class='empty-state'>{{ $t('customer.emptyProject') }}</div>
                <div v-else-if='filteredProjectList.length === 0' class='empty-state'>
                    {{ $t('customer.emptySearchProject') }}
                </div>
                <div v-else class='project-list scroll-list'>
                    <button
                        v-for='project in filteredProjectList'
                        :key='project.name'
                        class='project-item'
                        type='button'
                        @click='selectProject(project)'>
                        <img v-if='project.icon' :src='project.icon' class='project-icon' alt='' />
                        <span v-else class='project-icon project-icon-text'>{{ getProjectInitial(project) }}</span>
                        <span class='project-name'>{{ getProjectDisplayName(project) }}</span>
                        <span class='chevron'>›</span>
                    </button>
                </div>
            </div>
        </section>
        
        <section v-else class='customer-frame'>
            <template v-if='!selectedQa'>
                <header class='customer-topbar'>
                    <button
                        v-if='canSwitchProject'
                        class='topbar-action'
                        type='button'
                        @click='backToProjectPicker'>
                        {{ $t('customer.switchGame') }}
                    </button>
                    <h1>{{ $t('customer.title') }}</h1>
                </header>
                
                <main class='customer-body'>
                    <section v-if='userInfo' class='player-card'>
                        <h2>{{ $t('customer.playerInfo') }}</h2>
                        <div class='user-id-line'>
                            <span class='user-id-label'>UserID:</span>
                            <span class='user-id-value user-id-full'>{{ displayUserId }}</span>
                            <span class='user-id-value user-id-short'>{{ shortDisplayUserId }}</span>
                            <button class='copy-button' type='button' @click='copyUserInfo'>{{ $t('customer.copy') }}
                            </button>
                        </div>
                    </section>
                    
                    <nav v-if='groupList.length > 0' class='group-tabs'>
                        <button
                            v-for='group in groupList'
                            :key='group.id'
                            class='group-tab'
                            :class='{ active: String(group.id) === activeGroupId }'
                            type='button'
                            @click='selectGroup(group)'>
                            {{ group.name }}
                        </button>
                    </nav>
                    
                    <section class='question-panel'>
                        <div v-if='qaLoading' class='empty-state compact'>{{ $t('common.loading') }}</div>
                        <div v-else-if='groupList.length === 0' class='empty-state compact'>{{ $t('customer.emptyGroup')
                            }}
                        </div>
                        <div v-else-if='qaList.length === 0' class='empty-state compact'>{{ $t('customer.emptyQa') }}
                        </div>
                        <div v-else class='question-list scroll-list'>
                            <button
                                v-for='qa in qaList'
                                :key='qa.id'
                                class='question-item'
                                type='button'
                                @click='openQa(qa)'>
                                <span>{{ qa.question }}</span>
                                <span class='chevron'>›</span>
                            </button>
                        </div>
                    </section>
                    
                    <section class='contact-panel'>
                        <h2>{{ $t('customer.noAnswer') }}</h2>
                        <p>{{ $t('customer.contactTip') }}</p>
                        <button
                            v-if='contactList.length > 0'
                            type='button'
                            class='contact-entry'
                            @click='openContactDialog'>
                            <i class='el-icon-service'></i>
                            <span>{{ $t('customer.contactSupport') }}</span>
                        </button>
                        <div v-else class='empty-state compact'>{{ $t('customer.emptyContact') }}</div>
                    </section>
                </main>
            </template>
            
            <template v-else>
                <header class='customer-topbar detail-topbar'>
                    <button class='back-icon' type='button' @click='selectedQa = null'>‹</button>
                    <h1>{{ $t('customer.questionDetail') }}</h1>
                </header>
                
                <article class='answer-body'>
                    <section class='solution-section'>
                        <h2>{{ $t('customer.solution') }}</h2>
                        <div class='question-detail-title'>{{ selectedQa.question }}</div>
                        <div class='answer-content'>{{ selectedQa.answer }}</div>
                    </section>
                    
                    <section class='contact-panel detail-contact'>
                        <h2>{{ $t('customer.unresolved') }}</h2>
                        <p>{{ $t('customer.contactTip') }}</p>
                        <button
                            v-if='contactList.length > 0'
                            type='button'
                            class='contact-entry'
                            @click='openContactDialog'>
                            <i class='el-icon-service'></i>
                            <span>{{ $t('customer.contactSupport') }}</span>
                        </button>
                        <div v-else class='empty-state compact'>{{ $t('customer.emptyContact') }}</div>
                    </section>
                </article>
            </template>
        </section>
        
        <el-dialog
            :title="$t('customer.contactDialogTitle')"
            :visible.sync='contactDialogVisible'
            custom-class='customer-contact-dialog'
            append-to-body>
            <p class='contact-dialog-tip'>{{ $t('customer.contactMethodTip') }}</p>
            <div class='contact-dialog-list'>
                <div v-for='item in contactList' :key='item.id' class='contact-row'>
                    <div class='contact-row-main'>
                        <div class='contact-row-title'>
                            <i :class='getContactIcon(item.type)'></i>
                            <span>{{ getContactLabel(item.type) }}</span>
                        </div>
                        <div v-if='shouldShowContactValue(item)' class='contact-row-value'>{{ item.contactValue }}</div>
                        <div v-else class='contact-row-value muted'>{{ $t('customer.wechatContactTip') }}</div>
                    </div>
                    <button
                        class='contact-open-button'
                        type='button'
                        @click='openContact(item)'>
                        {{ getContactActionText(item.type) }}
                    </button>
                </div>
            </div>
            <div v-if='userInfo' class='dialog-user-tip'>
                <span class='dialog-user-tip-full'>{{ $t('customer.contactWithUserId', { userId: displayUserId })
                    }}</span>
                <span class='dialog-user-tip-short'>{{ $t('customer.contactWithUserId', { userId: shortDialogUserId })
                    }}</span>
                <button type='button' @click='copyUserInfo'>{{ $t('customer.copy') }}</button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { accountApi } from '@/service/api/accountApi';
import { customerApi } from '@/service/api/customerApi';
import { projectApi } from '@/service/api/projectApi';
import { EnumLocalStore } from '@/constant/enums';
import { localStg } from '@/utils/storage/local';
import { message } from '@/utils/message';

export default {
    name: 'CustomerSupport',
    data() {
        return {
            selectedProjectName: '',
            projectFromQuery: false,
            projectSearch: '',
            userInfo: null,
            projectList: [],
            projectLoading: false,
            contactList: [],
            contactDialogVisible: false,
            groupList: [],
            qaList: [],
            activeGroupId: '',
            selectedQa: null,
            qaLoading: false
        };
    },
    computed: {
        isEnglish() {
            return (this.$i18n.locale || 'zh-CN').startsWith('en');
        },
        serverType() {
            return localStg.get(EnumLocalStore.region) === 'oversea' ? 'OVERSEAS' : 'MAINLAND';
        },
        canSwitchProject() {
            return !this.projectFromQuery && this.projectList.length > 1;
        },
        filteredProjectList() {
            const keyword = this.projectSearch.trim().toLowerCase();
            if (!keyword) {
                return this.projectList;
            }
            return this.projectList.filter(project => {
                const name = this.getProjectDisplayName(project).toLowerCase();
                const rawName = (project.name || '').toLowerCase();
                const cnName = (project.cnName || '').toLowerCase();
                return name.includes(keyword) || rawName.includes(keyword) || cnName.includes(keyword);
            });
        },
        displayUserId() {
            return this.userInfo && (this.userInfo.userId) ? (this.userInfo.userId) : '-';
        },
        shortDisplayUserId() {
            if (!this.displayUserId || this.displayUserId === '-') {
                return '-';
            }
            return this.displayUserId.length > 12 ? `${this.displayUserId.slice(0, 12)}...` : this.displayUserId;
        },
        shortDialogUserId() {
            if (!this.displayUserId || this.displayUserId === '-') {
                return '-';
            }
            return this.displayUserId.length > 10 ? `${this.displayUserId.slice(0, 10)}...` : this.displayUserId;
        }
    },
    created() {
        const projectName = this.$route.query.projectName || '';
        this.selectedProjectName = projectName;
        this.projectFromQuery = !!projectName;
        this.queryUserInfo();
        this.fetchProjectList();
        this.queryContactList();
        if (this.selectedProjectName) {
            this.queryGroupList();
        }
    },
    methods: {
        async fetchProjectList() {
            this.projectLoading = true;
            try {
                const result = await projectApi.queryAllProject(this.serverType);
                if (result.code === 200 && result.data) {
                    this.projectList = result.data;
                }
            } catch (error) {
                message.error(this.$t('customer.emptyProject'));
            } finally {
                this.projectLoading = false;
            }
        },
        queryUserInfo() {
            if (!localStg.get(EnumLocalStore.jwtTokenName)) {
                this.userInfo = null;
                return;
            }
            accountApi.queryUserDetailInfo().then(response => {
                if (response.code === 200) {
                    this.userInfo = response.data;
                }
            });
        },
        queryContactList() {
            customerApi.queryCustomerContact({ serverType: this.serverType }).then(response => {
                if (response.code === 200) {
                    this.contactList = response.data || [];
                }
            });
        },
        queryGroupList() {
            if (!this.selectedProjectName) {
                this.groupList = [];
                this.qaList = [];
                return;
            }
            customerApi.queryCustomerQaGroup({ projectName: this.selectedProjectName }).then(response => {
                if (response.code === 200) {
                    this.groupList = response.data || [];
                    this.activeGroupId = this.groupList.length > 0 ? String(this.groupList[0].id) : '';
                    this.selectedQa = null;
                    this.queryQaList();
                }
            });
        },
        queryQaList() {
            if (!this.activeGroupId) {
                this.qaList = [];
                return;
            }
            this.qaLoading = true;
            customerApi.queryCustomerQa({ groupId: Number(this.activeGroupId) }).then(response => {
                if (response.code === 200) {
                    this.qaList = response.data || [];
                }
                this.qaLoading = false;
            }).catch(() => {
                this.qaLoading = false;
            });
        },
        selectProject(project) {
            this.selectedProjectName = project.name;
            this.projectFromQuery = false;
            this.$router.replace({
                path: '/customer',
                query: Object.assign({}, this.$route.query, { projectName: project.name })
            });
            this.queryGroupList();
        },
        backToProjectPicker() {
            this.selectedProjectName = '';
            this.activeGroupId = '';
            this.selectedQa = null;
            this.qaList = [];
            const query = Object.assign({}, this.$route.query);
            delete query.projectName;
            this.$router.replace({ path: '/customer', query });
        },
        selectGroup(group) {
            this.activeGroupId = String(group.id);
            this.selectedQa = null;
            this.queryQaList();
        },
        openQa(qa) {
            this.selectedQa = qa;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        openContactDialog() {
            this.contactDialogVisible = true;
        },
        openContact(contact) {
            if (!contact || !contact.contactValue) {
                return;
            }
            window.location.href = this.getContactHref(contact);
        },
        getContactHref(contact) {
            if (!contact || !contact.contactValue) {
                return 'javascript:;';
            }
            if (contact.type === 'QQ') {
                return `mqqwpa://im/chat?chat_type=wpa&uin=${contact.contactValue}&version=1&src_type=web`;
            }
            if (contact.type === 'EMAIL') {
                return `mailto:${contact.contactValue}`;
            }
            return contact.contactValue;
        },
        getContactLabel(type) {
            if (type === 'QQ') {
                return this.$t('customer.qq');
            }
            if (type === 'WECHAT_WORK') {
                return this.$t('customer.wechat');
            }
            if (type === 'EMAIL') {
                return this.$t('customer.email');
            }
            return type;
        },
        getContactActionText(type) {
            if (type === 'QQ') {
                return this.$t('customer.openQQ');
            }
            if (type === 'WECHAT_WORK') {
                return this.$t('customer.openWechat');
            }
            if (type === 'EMAIL') {
                return this.$t('customer.sendEmail');
            }
            return this.$t('customer.openContact');
        },
        getContactIcon(type) {
            if (type === 'QQ') {
                return 'el-icon-chat-dot-round';
            }
            if (type === 'EMAIL') {
                return 'el-icon-message';
            }
            return 'el-icon-service';
        },
        shouldShowContactValue(contact) {
            return contact && contact.contactValue && contact.type !== 'WECHAT_WORK';
        },
        getProjectDisplayName(project) {
            if (!project) {
                return '';
            }
            return this.isEnglish ? (project.name || project.cnName) : (project.cnName || project.name);
        },
        getProjectInitial(project) {
            const name = this.getProjectDisplayName(project);
            return name ? name.slice(0, 1).toUpperCase() : '?';
        },
        copyUserInfo() {
            const content = `UserID: ${this.displayUserId}`;
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(content).then(() => message.success(this.$t('customer.copied')));
                return;
            }
            const textarea = document.createElement('textarea');
            textarea.value = content;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            message.success(this.$t('customer.copied'));
        }
    }
};
</script>

<style scoped>
.customer-page {
    width: 100%;
    color: #303133;
    box-sizing: border-box;
}

.customer-frame {
    width: 100%;
    min-height: 560px;
    background: #fff;
    border: 1px solid #DCDFE6;
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 12px 32px rgba(15, 35, 65, 0.08);
}

.customer-topbar {
    position: relative;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 56px;
    border-bottom: 1px solid #E4E7ED;
    box-sizing: border-box;
    background: #fff;
}

.customer-topbar h1 {
    max-width: 100%;
    margin: 0;
    overflow: hidden;
    color: #303133;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.35;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.topbar-action {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    border: 0;
    background: transparent;
    color: #409EFF;
    font-size: 13px;
    cursor: pointer;
}

.customer-body,
.answer-body,
.project-entry-body {
    padding: 16px;
    box-sizing: border-box;
}

.project-entry-body {
    max-width: 640px;
    margin: 0 auto;
}

.project-entry-body h2,
.player-card h2,
.contact-panel h2,
.solution-section h2 {
    margin: 0;
    color: #303133;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
}

.project-entry-body p,
.contact-panel p {
    margin: 6px 0 14px;
    color: #909399;
    font-size: 13px;
    line-height: 1.5;
}

.contact-panel h2 {
    font-size: 15px;
    line-height: 1.35;
}

.contact-panel p {
    margin: 2px 0 5px;
    line-height: 1.4;
}

.project-search {
    margin-bottom: 12px;
}

.scroll-list {
    overflow-y: auto;
    padding-right: 4px;
}

.project-list {
    display: grid;
    gap: 10px;
    max-height: 520px;
}

.question-list {
    max-height: 330px;
}

.project-list::-webkit-scrollbar,
.question-list::-webkit-scrollbar {
    width: 6px;
}

.project-list::-webkit-scrollbar-thumb,
.question-list::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background: #DCDFE6;
}

.player-card {
    min-height: 74px;
    margin-bottom: 12px;
    padding: 14px 16px;
    border: 1px solid #DCDFE6;
    border-radius: 7px;
    background: #F5F7FA;
    box-sizing: border-box;
}

.user-id-line {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
    color: #606266;
    font-size: 14px;
    line-height: 1.4;
}

.user-id-value {
    color: #303133;
    overflow-wrap: anywhere;
}

.user-id-short {
    display: none;
}

.copy-button {
    flex: 0 0 auto;
    height: 26px;
    padding: 0 8px;
    border: 0;
    background: transparent;
    color: #409EFF;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
}

.group-tabs {
    display: flex;
    gap: 32px;
    margin: 0 -16px;
    padding: 0 16px;
    overflow-x: auto;
    border-bottom: 1px solid #E4E7ED;
    scrollbar-width: none;
}

.group-tabs::-webkit-scrollbar {
    display: none;
}

.group-tab {
    position: relative;
    flex: 0 0 auto;
    height: 44px;
    border: 0;
    background: transparent;
    color: #606266;
    font-size: 15px;
    line-height: 44px;
    cursor: pointer;
    white-space: nowrap;
}

.group-tab.active {
    color: #409EFF;
    font-weight: 700;
}

.group-tab.active::after {
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    border-radius: 2px;
    background: #409EFF;
    content: '';
}

.question-panel {
    padding: 14px 0 6px;
}

.question-item,
.project-item {
    width: 100%;
    min-height: 48px;
    margin-bottom: 8px;
    padding: 0 12px;
    border: 1px solid #E4E7ED;
    border-radius: 7px;
    background: #fff;
    box-sizing: border-box;
    color: #303133;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-size: 15px;
    text-align: left;
}

.project-item {
    min-height: 62px;
    margin-bottom: 0;
    padding: 10px 14px;
}

.question-item:active,
.project-item:active,
.copy-button:active {
    background: #ECF5FF;
}

.question-item span:first-child,
.project-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.chevron {
    flex: 0 0 auto;
    color: #C0C4CC;
    font-size: 30px;
    font-weight: 300;
    line-height: 1;
}

.contact-panel {
    margin: 4px -16px -16px;
    padding: 12px 16px 14px;
    background: #fff;
    text-align: center;
}

.contact-entry {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 28px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #409EFF;
    cursor: pointer;
    font-size: 14px;
    font-weight: 700;
    box-sizing: border-box;
}

.contact-entry:active {
    opacity: 0.8;
}

.detail-topbar {
    padding: 0 48px;
}

.back-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    width: 28px;
    height: 36px;
    transform: translateY(-50%);
    border: 0;
    background: transparent;
    color: #303133;
    cursor: pointer;
    font-size: 34px;
    font-weight: 300;
    line-height: 1;
}

.answer-body {
    min-height: 510px;
}

.solution-section {
    min-height: 280px;
    padding-top: 12px;
}

.solution-section h2 {
    color: #409EFF;
}

.question-detail-title {
    margin-top: 16px;
    color: #303133;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.6;
    overflow-wrap: anywhere;
}

.answer-content {
    margin-top: 16px;
    white-space: pre-wrap;
    color: #303133;
    font-size: 15px;
    line-height: 1.8;
}

.detail-contact {
    margin-top: 24px;
}

.project-entry {
    min-height: 420px;
}

.project-icon {
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
    border-radius: 9px;
    object-fit: cover;
}

.project-icon-text {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ECF5FF;
    color: #409EFF;
    font-weight: 700;
}

.project-name {
    flex: 1 1 auto;
    font-weight: 700;
}

.empty-state {
    padding: 42px 12px;
    color: #909399;
    font-size: 14px;
    text-align: center;
}

.empty-state.compact {
    padding: 24px 12px;
}


/deep/ .customer-contact-dialog {
    width: calc(100% - 32px) !important;
    max-width: 520px;
}

/deep/ .customer-contact-dialog .el-dialog__body {
    padding: 16px 20px 20px;
}

.contact-dialog-tip {
    margin: 0 0 10px;
    color: #909399;
    font-size: 13px;
    line-height: 1.5;
}

.contact-dialog-list {
    display: grid;
    gap: 10px;
    max-height: 360px;
    overflow-y: auto;
    padding-right: 4px;
}

.contact-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px;
    border: 1px solid #E4E7ED;
    border-radius: 7px;
    background: #fff;
}

.contact-row-main {
    min-width: 0;
}

.contact-row-title {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #303133;
    font-size: 14px;
    font-weight: 700;
}

.contact-row-title i {
    color: #409EFF;
}

.contact-row-value {
    margin-top: 6px;
    color: #606266;
    font-size: 13px;
    line-height: 1.4;
    overflow-wrap: anywhere;
}

.contact-row-value.muted {
    color: #909399;
}

.contact-open-button {
    flex: 0 0 auto;
    border: 0;
    background: transparent;
    color: #409EFF;
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
}

.dialog-user-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 7px;
    background: #F5F7FA;
    color: #909399;
    font-size: 13px;
    line-height: 1.4;
}

.dialog-user-tip button {
    flex: 0 0 auto;
    border: 0;
    background: transparent;
    color: #409EFF;
    cursor: pointer;
    font-size: 13px;
}

.dialog-user-tip-short {
    display: none;
}

@media screen and (max-width: 768px) {
    .user-id-full {
        display: none;
    }
    
    .user-id-short {
        display: inline;
    }
    
    .dialog-user-tip-full {
        display: none;
    }
    
    .dialog-user-tip-short {
        display: inline;
    }
}

@media screen and (max-width: 450px) {
    .customer-frame {
        min-height: 520px;
        border-radius: 18px;
    }
    
    .customer-body,
    .answer-body,
    .project-entry-body {
        padding: 14px;
    }
    
    .group-tabs {
        gap: 30px;
        margin: 0 -14px;
        padding: 0 14px;
    }
    
    .question-list {
        max-height: 260px;
    }
    
    .project-list {
        max-height: 560px;
    }
    
    .contact-panel {
        margin-right: -14px;
        margin-left: -14px;
        margin-bottom: -14px;
        padding: 12px 14px 14px;
    }
    
    .contact-row {
        align-items: flex-start;
    }
}
</style>
